import { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Error } from '@components/error/src/error';
import { Input } from '@components/input';
import { LabelPrimary } from '@components/label';
import { SectionDisclosure } from '@components/section-disclosure';
import classNames from 'classnames';

import { VatRateSectionProps } from './vat-rate-section.type';

export const VAT_RATE_SECTION_TEST_ID = 'vat-rate-section';

const FIELD_NAME = 'vatRate';

const formatRaw = (next: number | null): string => (next === null ? '' : String(next));

export const VatRateSection = ({
  value,
  onChange,
  open,
  onToggleOpen,
  disabled = false,
  errors,
  dataTestId = VAT_RATE_SECTION_TEST_ID,
}: VatRateSectionProps) => {
  const { t } = useTranslation();

  const [rawInput, setRawInput] = useState<string>(formatRaw(value.vatRate));

  useEffect(() => {
    const parsedRaw = rawInput === '' ? null : Number.parseFloat(rawInput);
    const normalisedRaw = Number.isNaN(parsedRaw) ? null : parsedRaw;
    if (normalisedRaw !== value.vatRate) {
      setRawInput(formatRaw(value.vatRate));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.vatRate]);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value.replace(',', '.');
    if (raw === '') {
      setRawInput('');
      onChange({ vatRate: null });
      return;
    }
    if (!/^[\d.]*$/.test(raw)) return;
    if ((raw.match(/\./g) ?? []).length > 1) return;
    setRawInput(raw);
    const parsed = Number.parseFloat(raw);
    if (!Number.isNaN(parsed)) {
      onChange({ vatRate: parsed });
    }
  };

  const hasError = !!errors?.vatRate;
  const fieldId = `${dataTestId}-field`;
  const errorId = `${dataTestId}-error`;

  return (
    <SectionDisclosure
      title={t('offers:admissionVatRateSectionTitle')}
      open={open}
      onToggleOpen={onToggleOpen}
      dataTestId={`${dataTestId}-disclosure`}>
      <section
        data-testid={dataTestId}
        className={classNames('flex flex-col md:flex-row md:items-start gap-4 md:gap-6 w-full py-4 md:py-6', {
          'cursor-not-allowed opacity-50 pointer-events-none': disabled,
        })}>
        <div className='w-full md:w-48'>
          <LabelPrimary htmlFor={fieldId} hasError={hasError}>
            {t('offers:admissionVatRateSectionTitle')}
          </LabelPrimary>
          <Input
            id={fieldId}
            name={FIELD_NAME}
            type='text'
            inputMode='decimal'
            placeholder='0'
            suffix='%'
            value={rawInput}
            onChange={handleInputChange}
            disabled={disabled}
            readOnly={disabled}
            hasError={hasError}
            aria-invalid={hasError}
            aria-describedby={hasError ? errorId : undefined}
            data-testid={`${dataTestId}-input`}
          />
          {errors?.vatRate && (
            <Error id={errorId} dataTestId={errorId}>
              {errors.vatRate}
            </Error>
          )}
        </div>
        <p className='text-secondary text-body-s md:pt-7 md:max-w-96'>
          {t('offers:admissionVatRateSectionDescription')}
        </p>
      </section>
    </SectionDisclosure>
  );
};
